import { getPaletteColors } from '~/utils/helpers/css';

export const SYSTEM_PUBLIC_HOLIDAY_EVENT = 'SYSTEM_PUBLIC_HOLIDAY_EVENT';

export const absenceEventColors = {
  HOLIDAY_GREEN: '#43a047',
  MILITARY_REHEARSAL_BROWN: '#a06a34',
  SICK_LEAVE_RED: '#d70000',
  MATERNITY_LEAVE_BLUE: '#07a2c5',
  STUDY_LEAVE_YELLOW: '#ffd200',
  CUSTOM_HOLIDAY_PURPLE: '#800075',
  PUBLIC_HOLIDAY: '#196400',
  OTHER: '#1457cb',
};

export enum AbsenceLegendKeys {
  Holiday = 'HOLIDAY',
  SickLeave = 'SICK_LEAVE',
  PublicHoliday = 'publicHoliday',
  MaternityLeave = 'MATERNITY_LEAVE',
  MilitaryRehearsal = 'MILITARY_REHEARSAL',
  StudyLeave = 'STUDY_LEAVE',
  Other = 'OTHER',
}

// Generate color for string
export const hashCode = (str: string): number => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return hash;
};

export const hashForColor = (str: string, charLength?: number): number => {
  const hashLength = charLength ?? 16;
  const hash = hashCode(str);
  const multiply = Math.ceil(hashLength / hash.toString().length);

  return parseInt(
    Array.from(Array(multiply))
      .map((_v, index) => hash * (index + 1))
      .join('')
      .substring(0, hashLength),
  );
};

export const intToRGB = (i: number): string => {
  let c = `#${(i & 0x00ffffff).toString(16).toUpperCase()}`;
  c = c + '000000'.substring(0, 7 - c.length);

  // Check if the color is white or a variation of white
  if (
    c === '#FFFFFF' ||
    c === '#FFF' ||
    c === '#FFFFF0' ||
    c === '#F8F8FF' ||
    c === '#FFFAFA'
  ) {
    c = `#${((i + 1) & 0x00ffffff).toString(16).toUpperCase()}`;
    c = c + '00000'.substring(0, 6 - c.length);
  }

  return c;
};

export const getGeneratedColorForString = (code: string): string => {
  const hashCharLength = 9;
  return intToRGB(hashForColor(code, hashCharLength));
};

// Dynamic color functions
export enum DefaultAbsenceReasonCodes {
  Holiday = 'HOLIDAY',
  MilitaryRehearsal = 'MILITARY_REHEARSAL',
  SickLeave = 'SICK_LEAVE',
  MaternityLeave = 'MATERNITY_LEAVE',
  StudyLeave = 'STUDY_LEAVE',
  Vacation = 'VACATION',
  Other = 'OTHER',
}

export const getDefaultAbsenceReasonCodeColor = (
  code: DefaultAbsenceReasonCodes | string,
) => {
  const {
    HOLIDAY_GREEN,
    MILITARY_REHEARSAL_BROWN,
    SICK_LEAVE_RED,
    MATERNITY_LEAVE_BLUE,
    STUDY_LEAVE_YELLOW,
    OTHER,
  } = absenceEventColors;

  const colorMap: { [key in DefaultAbsenceReasonCodes]: string } = {
    [DefaultAbsenceReasonCodes.Holiday]: HOLIDAY_GREEN,
    [DefaultAbsenceReasonCodes.MilitaryRehearsal]: MILITARY_REHEARSAL_BROWN,
    [DefaultAbsenceReasonCodes.SickLeave]: SICK_LEAVE_RED,
    [DefaultAbsenceReasonCodes.MaternityLeave]: MATERNITY_LEAVE_BLUE,
    [DefaultAbsenceReasonCodes.StudyLeave]: STUDY_LEAVE_YELLOW,
    [DefaultAbsenceReasonCodes.Vacation]: HOLIDAY_GREEN,
    [DefaultAbsenceReasonCodes.Other]: OTHER,
  };

  if (
    Object.values(DefaultAbsenceReasonCodes)
      .map((c) => c.toString())
      .includes(code)
  ) {
    return colorMap[code as DefaultAbsenceReasonCodes];
  }

  return undefined;
};

type AbsenceReasonCodeColor = {
  code: string;
  color: string;
};

export const getColorsForAbsenceReasonCodes = (
  reasonCodes: string[],
): AbsenceReasonCodeColor[] => {
  // Default color set
  const defaultCodes = Object.values(DefaultAbsenceReasonCodes).filter((c) =>
    reasonCodes.includes(c),
  );
  const defaultColors = defaultCodes.map(
    (code): AbsenceReasonCodeColor => ({
      code,
      color: getDefaultAbsenceReasonCodeColor(code)!,
    }),
  );

  // Palette color set
  const otherCodes = reasonCodes.filter(
    (c) => !defaultCodes.map((c) => c.toString()).includes(c),
  );
  const otherColorPalette = getPaletteColors(otherCodes.length);

  const otherPaletteColors = otherCodes.slice(0, otherColorPalette.length).map(
    (code, index): AbsenceReasonCodeColor => ({
      code,
      color: otherColorPalette[index],
    }),
  );

  // Generated color set
  const otherStringGeneratedColors = otherCodes
    .slice(otherColorPalette.length)
    .map(
      (code): AbsenceReasonCodeColor => ({
        code,
        color: getGeneratedColorForString(code),
      }),
    );

  return [
    ...defaultColors,
    ...otherPaletteColors,
    ...otherStringGeneratedColors,
  ];
};

export const resolveAbsenceColorByCode =
  (codeColors: AbsenceReasonCodeColor[]) => (code: string) => {
    return (
      codeColors.find((c) => c.code === code)?.color ??
      getGeneratedColorForString(code)
    );
  };
